import { useCallback, useEffect, useRef, useState } from 'react'
import { DateTerm } from '../../../../utils/date'
import DateVO from '../../../../vo/DateVO'
import { SearchUnit } from '../../../components/headers/HeaderBar/DateRangeSelector'
import { ROW_HEIGHT } from '../../../containers/BulkSheet'
import uiStates, {
  UiStateKey,
  UiStateScope,
} from '../../../../lib/commons/uiStates'

type StoredProjectClosingStatusPageState = {
  rowHeight: ROW_HEIGHT
}

export const usePageState = (functionUuid: string) => {
  const now = DateVO.now()
  const searchUnit = SearchUnit.MONTH
  const [rowHeight, setRowHeight] = useState<ROW_HEIGHT>(ROW_HEIGHT.SMALL)
  const [initialized, setInitialized] = useState(false)

  const storePageState = useCallback(
    async (value: StoredProjectClosingStatusPageState) => {
      uiStates.update(
        {
          key: UiStateKey.PageState,
          scope: UiStateScope.User,
          value: JSON.stringify(value),
        },
        functionUuid
      )
    },
    [functionUuid]
  )

  useEffect(() => {
    if (!initialized) return
    storePageState({
      rowHeight,
    })
  }, [initialized, storePageState, rowHeight])

  const restorePageState = useCallback(async () => {
    const response = await uiStates.get({
      applicationFunctionUuid: functionUuid,
      key: UiStateKey.PageState,
      scope: UiStateScope.User,
    })
    if (response.json.value) {
      const state: StoredProjectClosingStatusPageState = JSON.parse(
        response.json.value
      )
      setRowHeight(state.rowHeight ?? ROW_HEIGHT.SMALL)
    }

    setInitialized(true)
  }, [functionUuid])

  const [displayDateTerm, setDisplayDateTerm] = useState<DateTerm>({
    startDate: now.getFirstDayOfMonth().format(),
    endDate: now.getLastDayOfMonth().format(),
  })

  const monthDateTerm = useRef<DateTerm>({
    startDate: now.getFirstDayOfMonth().format(),
    endDate: now.getLastDayOfMonth().format(),
  })

  const handleMonthChange = useCallback((year: number, month: number) => {
    const startDate = new DateVO(`${year}-${String(month).padStart(2, '0')}-01`)
    const nextMonth = month === 12 ? 1 : month + 1
    const nextYear = month === 12 ? year + 1 : year
    const nextMonthFirstDay = new DateVO(
      `${nextYear}-${String(nextMonth).padStart(2, '0')}-01`
    )
    const endDate = nextMonthFirstDay.addDays(-1)
    setDisplayDateTerm({
      startDate: startDate.serialize(),
      endDate: endDate.serialize(),
    })
  }, [])

  useEffect(() => {
    setDisplayDateTerm(monthDateTerm.current)
    restorePageState()
  }, [restorePageState])

  return {
    initialized,
    searchUnit,
    rowHeight,
    setRowHeight,
    displayDateTerm,
    setDisplayDateTerm,
    handleMonthChange,
  }
}
